import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChevronsRight,
  CircleHelp,
  LogOut,
  type LucideIcon,
  Search,
  UserRound,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavScroll } from './NavScroll.tsx';
import { useShell } from './slots.tsx';
import type { Me } from '../api.ts';
import { logout } from '../auth.tsx';
import { itemsOf, locate, type NavArea, opensMenu } from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { FooterIcon, settingsEntry, SidebarNav } from './menu.tsx';

/** Lateral aberta: logo, busca, lista e, numa linha no rodapé, a pessoa, Configuração, Ajuda e Sair. */
export function Sidebar({
  areas,
  me,
  idPrefix,
  initialArea,
  headerAction,
  onSearch,
  onNavigate,
}: {
  areas: NavArea[];
  me: Me;
  idPrefix: string;
  initialArea?: string;
  /** Recolher (computador) ou fechar (celular). */
  headerAction: ReactNode;
  onSearch: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { settingsArea, settings } = settingsEntry(areas);
  const inSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  const navLabels = useShell()?.options.navLabels ?? 'icons';
  return (
    <div
      data-nav-icons={navLabels === 'text' ? 'hide' : undefined}
      className="ui-sidebar flex h-full min-h-0 flex-col gap-3 px-3 py-4"
    >
      <div className="flex items-start justify-between gap-2 px-1">
        <Link
          to="/"
          onClick={onNavigate}
          aria-label={t('nav.homeBrand')}
          className="flex flex-col gap-1"
        >
          <img
            src={look.logoUrl}
            alt=""
            className="ui-logo w-auto max-w-52 self-start object-contain"
          />
          <span className="text-xs text-muted">{look.appName}</span>
        </Link>
        {headerAction}
      </div>
      <button
        type="button"
        onClick={onSearch}
        className="flex min-h-10 shrink-0 items-center justify-between gap-2 rounded-md border border-rule bg-paper px-3 py-2 text-left text-[13px] text-muted hover:border-control-border"
      >
        <span className="flex items-center gap-2">
          <Search size={14} aria-hidden />
          {t('nav.search')}
        </span>
        <kbd className="hidden rounded border border-rule bg-sheet px-1.5 font-mono text-[11px] md:inline">
          Ctrl K
        </kbd>
      </button>
      <NavScroll className="-mx-1 min-h-0 flex-1 px-1 py-1">
        <SidebarNav
          areas={areas}
          idPrefix={idPrefix}
          initialArea={initialArea}
          onNavigate={onNavigate}
        />
      </NavScroll>
      <div className="shrink-0 border-t border-rule pt-3">
        {/* Uma linha só: o nome (abre as configurações da pessoa), a engrenagem da Configuração,
            a Ajuda e Sair. Os ícones levam o nome na dica e para o leitor de tela. */}
        <div className="flex items-center gap-1 px-1">
          <Link
            to="/account"
            onClick={onNavigate}
            title={`${t('nav.account')} (${me.user.email})`}
            aria-current={pathname === '/account' ? 'page' : undefined}
            className={`flex min-h-9 min-w-0 flex-1 items-center rounded-md px-1.5 text-sm hover:bg-paper ${
              pathname === '/account' ? 'ui-nav-active font-medium' : 'font-medium text-ink'
            }`}
          >
            <span className="truncate">{me.user.displayName}</span>
          </Link>
          {settings && (
            <FooterIcon
              to={settings.to}
              label={t(settingsArea!.key)}
              icon={settingsArea!.icon}
              active={inSettings}
              onNavigate={onNavigate}
            />
          )}
          <FooterIcon
            to="/help"
            label={t('nav.help')}
            icon={CircleHelp}
            active={pathname === '/help'}
            onNavigate={onNavigate}
          />
          <button
            type="button"
            onClick={() => void logout()}
            className="flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-1.5 text-sm text-brand-text hover:underline"
          >
            <LogOut size={16} strokeWidth={1.75} aria-hidden />
            {t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Botão só com ícone da faixa recolhida: o nome vai na dica e para o leitor de tela. */
export const RAIL_BUTTON =
  'flex size-10 shrink-0 items-center justify-center rounded-md text-rail-text hover:bg-paper hover:text-ink';

/**
 * Lateral recolhida (ADR 0071): uma faixa de ícones com Início, a busca, os departamentos e, no pé,
 * Configuração, a pessoa, Ajuda e Sair. O clique num departamento abre a lateral nele.
 */
export function CollapsedRail({
  areas,
  onSearch,
  onExpand,
}: {
  areas: NavArea[];
  onSearch: () => void;
  onExpand: (areaId?: string) => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const look = usePlatformLook();
  const { settingsArea, settings } = settingsEntry(areas);
  const railLink = (to: string, label: string, Icon: LucideIcon, active: boolean) => (
    <Link
      to={to}
      aria-label={label}
      title={label}
      aria-current={active ? 'page' : undefined}
      className={`${RAIL_BUTTON} ${active ? 'ui-nav-active' : ''}`}
    >
      <Icon size={20} strokeWidth={1.75} aria-hidden />
    </Link>
  );
  const icon = (area: NavArea) => {
    const Icon = area.icon;
    const label = t(area.key);
    const active = area.id === current?.id;
    const className = `${RAIL_BUTTON} ${active ? 'ui-nav-active' : ''}`;
    const body = <Icon size={20} strokeWidth={1.75} aria-hidden />;
    // Departamento sem menu para abrir (opensMenu): vai direto para a página.
    if (!opensMenu(area))
      return (
        <Link
          key={area.id}
          to={itemsOf(area)[0]!.to}
          aria-label={label}
          title={label}
          aria-current={active ? 'true' : undefined}
          className={className}
        >
          {body}
        </Link>
      );
    return (
      <button
        key={area.id}
        type="button"
        onClick={() => onExpand(area.id)}
        aria-label={label}
        title={label}
        aria-current={active ? 'true' : undefined}
        className={className}
      >
        {body}
      </button>
    );
  };
  return (
    <div className="ui-sidebar flex h-full min-h-0 flex-col items-center gap-1 py-3">
      <Link to="/" aria-label={t('nav.homeBrand')} className="mb-1 flex shrink-0 py-1">
        <img src={look.symbolUrl} alt="" className="h-[26px] w-auto max-w-10 object-contain" />
      </Link>
      <button
        type="button"
        onClick={() => onExpand()}
        aria-label={t('nav.expand')}
        title={t('nav.expand')}
        className={`${RAIL_BUTTON} border border-control-border`}
      >
        <ChevronsRight size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onSearch}
        aria-label={t('nav.search')}
        title={`${t('nav.search')} (Ctrl K)`}
        className={RAIL_BUTTON}
      >
        <Search size={18} strokeWidth={1.75} aria-hidden />
      </button>
      <NavScroll className="flex min-h-0 flex-1 flex-col items-center gap-1 py-1">
        {areas.filter((a) => !a.bottom).map(icon)}
      </NavScroll>
      <div className="flex shrink-0 flex-col items-center gap-1 border-t border-rule pt-2">
        {settings &&
          railLink(
            settings.to,
            t(settingsArea!.key),
            settingsArea!.icon,
            current?.id === 'settings',
          )}
        {railLink('/account', t('nav.account'), UserRound, pathname === '/account')}
        {railLink('/help', t('nav.help'), CircleHelp, pathname === '/help')}
        <button
          type="button"
          onClick={() => void logout()}
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
          className={RAIL_BUTTON}
        >
          <LogOut size={20} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );
}
